import KRGlue from '@lyracom/embedded-form-glue';
import { useEffect, useId, useRef, useState } from 'react';
import Head from 'next/head';
import { toastError } from '../Common/toasters';
import { useRouter } from 'next/router';
import {
  acquirePaymentForm,
  releasePaymentForm,
  subscribePaymentForm,
} from '@/utils/paymentFormRegistry';
import { withTimeout } from '@/utils/withTimeout';
import { createAttemptGuard } from '@/utils/attemptGuard';
import {
  buildPayzenFormConfig,
  isPseAutoReturnEnabled,
} from '@/utils/payzenFormConfig';

// Bounded waits so a stalled Krypton load can never spin the loader forever.
const KRYPTON_INIT_TIMEOUT_MS = 15000; // loadLibrary + setFormConfig
const FORM_READY_TIMEOUT_MS = 15000; // renderElements -> onFormReady

const NEXT_PUBLIC_PAYZEN_PUBLIC_KEY = process.env
  .NEXT_PUBLIC_PAYZEN_PUBLIC_KEY as string;
const NEXT_PUBLIC_PAYZEN_API_ENDPOINT = process.env
  .NEXT_PUBLIC_PAYZEN_API_ENDPOINT as string;
const NEXT_PUBLIC_APP_URL = process.env.NEXT_PUBLIC_APP_URL as string;

// Feature flag: suppress PayZen's own PSE receipt so the buyer returns to
// Tikipal automatically (no manual "VOLVER A TIKIPAL" tap). OFF by default in
// production; enabled per-environment via NEXT_PUBLIC_PAYZEN_PSE_HIDE_RECEIPT.
// The env is read as a DIRECT member access so Next inlines it at build time
// (NEXT_PUBLIC_* is build-time static on the client).
const HIDE_PSE_RECEIPT = isPseAutoReturnEnabled(
  process.env.NEXT_PUBLIC_PAYZEN_PSE_HIDE_RECEIPT
);

type NormalizedClientAnswer = KRClientAnswer & {
  orderDetails: { orderId: string };
};

type Props = {
  formToken: string;
  // 'grid' (default, current behavior — method grid, card opens a popin),
  // 'smart-expanded' (Smart Form list with the card form expanded inline + other
  // methods like PSE visible, no popin), 'embedded-card' (card-only inline).
  displayMode?: 'grid' | 'smart-expanded' | 'embedded-card';
  // Optional, additive: fires once Krypton reports the form is ready (onFormReady).
  // Only the PSE one-click panel uses it (to know when window.KR.submit() is safe);
  // the card flow never passes it, so its behavior is unchanged.
  onReady?: () => void;
};

const PayzenCardForm = ({ formToken, displayMode = 'grid', onReady }: Props) => {
  const router = useRouter();
  const language = router.locale as SupportedLanguages;
  const instanceId = useId();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only the holder of the single-form lock renders Krypton / registers onSubmit.
  const [canRender, setCanRender] = useState(false);
  // Bumped by the Retry button to re-run init with the SAME token (no new order).
  const [retryTick, setRetryTick] = useState(0);

  // Per-attempt generation guard: a timed-out/superseded init continuation is
  // detected as stale and becomes a no-op (it cannot register callbacks or pay).
  const attemptGuardRef = useRef(createAttemptGuard());
  // True only while mounted — guards the submit handler against late calls.
  const mountedRef = useRef(true);
  // onSubmit/onError/onFormReady are registered exactly once per instance.
  const callbacksRegisteredRef = useRef(false);
  // Whether the form has ever reported ready (so retries close the loader fast).
  const everReadyRef = useRef(false);
  // Mirror of `loading` for use inside timers without stale closures.
  const loadingRef = useRef(true);
  const setLoadingSafe = (v: boolean) => {
    loadingRef.current = v;
    setLoading(v);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Acquire the single active-payment-form lock. In the normal flow only the
  // interacted instance has a token, so it acquires immediately; a second
  // instance (e.g. hidden desktop card) waits instead of mounting a rival form.
  useEffect(() => {
    const tryAcquire = () => {
      if (acquirePaymentForm(instanceId)) setCanRender(true);
    };
    tryAcquire();
    const unsubscribe = subscribePaymentForm(tryAcquire);
    return () => {
      unsubscribe();
      releasePaymentForm(instanceId);
      setCanRender(false);
    };
  }, [instanceId]);

  useEffect(() => {
    if (!formToken) {
      setLoadingSafe(false);
      setLoadError('No se pudo generar el formulario de pago. Por favor intenta de nuevo.');
      return;
    }

    // Wait for the single-form lock before initialising Krypton so two instances
    // can never register callbacks / be submit-eable at the same time.
    if (!canRender) return;

    let cancelled = false;
    const attempt = attemptGuardRef.current.begin();
    // Stale once a newer attempt starts (retry) or the component unmounts.
    const isStale = () => cancelled || !attempt.isCurrent();

    setLoadingSafe(true);
    setLoadError(null);

    const currentLanguage = language;
    let readyFallback: ReturnType<typeof setTimeout> | undefined;

    const FAILURE_MESSAGE =
      'No se pudo cargar el formulario de pago. Verifica tu conexión e intenta de nuevo.';

    async function setupPaymentForm() {
      try {
        // Bound the (cold) library load so a never-resolving poll cannot hang
        // the loader forever. A late resolution is swallowed by withTimeout and
        // additionally neutralised by the isStale() guard below.
        const { KR } = await withTimeout(
          KRGlue.loadLibrary(
            NEXT_PUBLIC_PAYZEN_API_ENDPOINT,
            NEXT_PUBLIC_PAYZEN_PUBLIC_KEY
          ),
          KRYPTON_INIT_TIMEOUT_MS
        );
        if (isStale()) return;

        // Baseline config is unchanged; when the flag is on we additionally
        // disable PayZen's receipt page for PSE ONLY (card stays identical).
        await KR.setFormConfig(
          buildPayzenFormConfig({
            formToken,
            language: currentLanguage,
            hidePseReceipt: HIDE_PSE_RECEIPT,
          })
        );
        if (isStale()) return;

        // Register event callbacks exactly ONCE per instance so a retry never
        // stacks duplicate handlers (which would double-POST the payment).
        if (!callbacksRegisteredRef.current) {
          // Close the loader on the verified "ready to submit" signal — not
          // merely after renderElements, which can resolve before PSE paints.
          await KR.onFormReady(() => {
            everReadyRef.current = true;
            if (mountedRef.current) {
              setLoadingSafe(false);
              onReady?.();
            }
          });

          // Surface provider errors for diagnostics without hiding the form or
          // touching payment-method configuration.
          await KR.onError((err: { errorCode?: string; errorMessage?: string }) => {
            if (mountedRef.current) {
              console.error('Krypton onError:', err?.errorCode || err?.errorMessage || err);
            }
          });

          await KR.onSubmit(async (paymentData) => {
            // A late/zombie attempt (unmounted) must never process a payment.
            if (!mountedRef.current) return false;
            // The buyer's real outcome is decided by purchase-success polling the
            // backend (IPN = source of truth), NOT by this client-side check.
            const orderId = (
              paymentData.clientAnswer as NormalizedClientAnswer
            )?.orderDetails?.orderId;
            try {
              // /api/validate-payment is only an HMAC INTEGRITY sanity check — it
              // does not tell us whether the payment succeeded. A non-200 (hash
              // mismatch, common on a PSE redirect-return) is logged for devs but
              // must NOT be shown to the buyer as a failure: it would be premature
              // (the IPN may be confirming) and wrongly invite a retry.
              const response = await fetch(
                `${NEXT_PUBLIC_APP_URL}/api/validate-payment`,
                {
                  method: 'POST',
                  body: JSON.stringify(paymentData),
                  headers: { 'Content-Type': 'application/json' },
                }
              );
              if (response.status !== 200) {
                console.error('validate-payment non-200:', response.status);
              }
            } catch (error) {
              console.error('Error processing payment:', error);
            }
            if (orderId) {
              // Always hand off to purchase-success, which shows confirming →
              // paid (QR) / declined / verifying based on the REAL status.
              router.push(`/purchase-success?orderId=${orderId}`);
            } else {
              // Genuine inability to proceed (no order reference at all) — this is
              // a real error, not a premature one.
              toastError(
                'No pudimos abrir la confirmación de tu pago. Revisa "Mis entradas" o tu correo.'
              );
            }
            return false;
          });

          callbacksRegisteredRef.current = true;
        }
        if (isStale()) return;

        await KR.renderElements('#myPaymentForm');
        if (isStale()) return;

        // Official Krypton API to set a static pay-button label (no
        // %amount-and-currency% template → no auto-formatted amount). Keeps the
        // real PayZen button + submit; not a DOM/textContent hack.
        await KR.button.setLabel(
          currentLanguage === 'en' ? 'Pay now' : 'Pagar ahora'
        );
        if (isStale()) return;

        if (everReadyRef.current) {
          // Re-render of an already-ready form (retry): close the loader now,
          // onFormReady may not fire again.
          setLoadingSafe(false);
        } else {
          // First render: wait for onFormReady, but never spin forever.
          readyFallback = setTimeout(() => {
            if (!isStale() && mountedRef.current && loadingRef.current) {
              setLoadingSafe(false);
              setLoadError(FAILURE_MESSAGE);
            }
          }, FORM_READY_TIMEOUT_MS);
        }
      } catch (error) {
        console.error('Error loading payment form:', error);
        if (!isStale() && mountedRef.current) {
          setLoadingSafe(false);
          setLoadError(FAILURE_MESSAGE);
        }
      }
    }

    setupPaymentForm();

    return () => {
      cancelled = true;
      if (readyFallback) clearTimeout(readyFallback);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formToken, canRender, retryTick]);

  // Another payment form instance currently holds the single-form lock.
  if (formToken && !canRender) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
        <div className="w-8 h-8 border-2 border-copper/30 border-t-copper rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Abriendo el formulario de pago…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
        <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <p className="text-sm text-gray-600 max-w-xs">{loadError}</p>
        <button
          onClick={() => {
            // In-place retry: re-init Krypton with the SAME formToken. Does not
            // reload the page, re-fetch a token, or create a new order/ticket.
            setLoadError(null);
            setLoadingSafe(true);
            setRetryTick((n) => n + 1);
          }}
          className="text-sm text-copper hover:underline font-semibold"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <>
      <Head>
        <link
          rel="stylesheet"
          href="https://static.lyra.com/static/js/krypton-client/V4.0/ext/neon-reset.min.css"
        />
        <style>{`
          #myPaymentForm {
            width: 100% !important;
            min-height: 400px !important;
            height: auto !important;
          }
          .kr-smart-form {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          .kr-payment-button,
          .kr-payment-method,
          [data-payment-method],
          .kr-embedded {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
        `}</style>
      </Head>
      <div className="flex flex-col items-center w-full relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center py-16 gap-3 z-10 bg-white">
            <div className="w-8 h-8 border-2 border-copper/30 border-t-copper rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Cargando formulario de pago…</p>
          </div>
        )}
        <div id="myPaymentForm" className="w-full" style={{ minHeight: '700px' }}>
          {/* eslint-disable react/no-unknown-property */}
          {displayMode === 'embedded-card' ? (
            <div className="kr-embedded"></div>
          ) : displayMode === 'smart-expanded' ? (
            <div className="kr-smart-form" {...{ 'kr-card-form-expanded': '' }}></div>
          ) : (
            <div className="kr-smart-form" {...{ 'kr-grid': '' }}></div>
          )}
          {/* eslint-enable react/no-unknown-property */}
        </div>
      </div>
    </>
  );
};

export default PayzenCardForm;

